import * as React from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not(:disabled)",
  "input:not(:disabled)",
  "textarea:not(:disabled)",
  "select:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

interface UseModalFocusOptions {
  open: boolean;
  dialogRef: React.RefObject<HTMLElement | null>;
  onDismiss: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

interface InertRestoreState {
  element: HTMLElement;
  ariaHidden: string | null;
  inert: boolean;
}

export function useModalFocus({
  open,
  dialogRef,
  onDismiss,
  returnFocusRef,
}: UseModalFocusOptions): void {
  const onDismissRef = React.useRef(onDismiss);
  onDismissRef.current = onDismiss;

  React.useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const portalContainer = dialog
      ? Array.from(document.body.children).find((child) => child.contains(dialog))
      : undefined;
    const inertRestoreState: InertRestoreState[] = [];

    for (const child of Array.from(document.body.children)) {
      if (
        !(child instanceof HTMLElement) ||
        child === portalContainer ||
        child.tagName === "SCRIPT" ||
        child.tagName === "STYLE"
      ) {
        continue;
      }
      inertRestoreState.push({
        element: child,
        ariaHidden: child.getAttribute("aria-hidden"),
        inert: child.hasAttribute("inert"),
      });
      child.setAttribute("aria-hidden", "true");
      child.setAttribute("inert", "");
    }

    const focusInitialElement = () => {
      const currentDialog = dialogRef.current;
      if (!currentDialog) return;
      const initial =
        currentDialog.querySelector<HTMLElement>("[data-autofocus]") ??
        currentDialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
        currentDialog;
      initial.focus();
    };
    const focusTimer = window.setTimeout(focusInitialElement, 0);

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      const currentDialog = dialogRef.current;
      if (!currentDialog) return;
      if (
        event.target instanceof Node &&
        document.querySelector("[data-skill-source-filter-menu]")?.contains(event.target)
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onDismissRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        currentDialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute("disabled"));
      if (focusable.length === 0) {
        event.preventDefault();
        currentDialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      inertRestoreState.forEach(({ element, ariaHidden, inert }) => {
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
        if (!inert) element.removeAttribute("inert");
      });
      window.setTimeout(() => {
        (returnFocusRef?.current ?? previouslyFocused)?.focus();
      }, 0);
    };
  }, [dialogRef, open, returnFocusRef]);
}
